import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { filtersGridClass } from '../filters-grid';

/** A column of the `list-page` skeleton: real header label + the shape of its cells. */
export interface SkeletonColumn {
  label: string;
  cell?: 'text' | 'two-line' | 'badge' | 'actions';
  /** Tailwind width class for the cell's shimmer block, e.g. 'w-24' */
  width?: string;
  /** Extra classes for the header cell, e.g. 'w-[40%]' or 'text-center' */
  headerClass?: string;
  align?: 'left' | 'center';
}

@Component({
  selector: 'app-skeleton-loader',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './skeleton-loader.component.html',
  // stat-card renders bare cards that must lay out as direct children of the parent's grid/flex
  host: { '[style.display]': "type === 'stat-card' ? 'contents' : null" },
})
export class SkeletonLoaderComponent {
  @Input() type: 'table' | 'card' | 'text' | 'stats' | 'dashboard' | 'followup-card' | 'drawer-followup' | 'list-page' | 'chart' | 'stat-card' = 'table';
  @Input() count: number = 5;
  // For table: widths of columns. e.g. ['w-10', 'w-40', 'w-20']
  @Input() tableColumns: string[] = ['w-20', 'w-48', 'w-32', 'w-24', 'w-24', 'w-20', 'w-8'];
  // For stats: number of stat cards to show (default 4 for dashboard)
  @Input() statsCount: number = 4;
  // For stats: 'simple' (single value) or 'detailed' (list of items)
  @Input() statsType: 'simple' | 'detailed' = 'simple';

  // --- list-page: mirrors a standard list screen (stats, filters, action bar, table, pagination) ---
  /** Titles of the stats cards; empty hides the stats row. */
  @Input() statsLabels: string[] = [];
  /** Labels of the filter fields; empty hides the filters card. */
  @Input() filterLabels: string[] = [];
  /** Width classes of standalone select boxes shown above the action bar (no filters card). */
  @Input() inlineFilterWidths: string[] = [];
  @Input() showSearch: boolean = true;
  /** Width classes of the buttons on the right of the action bar. */
  @Input() actionWidths: string[] = [];
  @Input() columns: SkeletonColumn[] = [];
  /** Minimum width in px of the `list-page` table, for wide tables that scroll horizontally. */
  @Input() tableMinWidth: number | null = null;
  @Input() showPagination: boolean = true;

  get filtersGridClass(): string {
    return filtersGridClass(this.filterLabels.length);
  }

  /** Height in px of a `chart` skeleton. */
  @Input() height: number = 280;

  /** Cards of a `stat-card` skeleton: real titles when `statsLabels` is set, otherwise `count` untitled cards. */
  get statCardItems(): string[] {
    return this.statsLabels.length ? this.statsLabels : new Array(this.count).fill('');
  }

  // Helper to iterate in template
  get range() {
    return new Array(this.count).fill(0);
  }

  // Helper for stats cards
  get statsRange() {
    return new Array(this.statsCount).fill(0);
  }
}
